<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { flowBar } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import type { IUsePlacesSearch } from '@/composition/store-setup/use-places-search';
  import type {
    IStoreSetupDraft,
    IStoreSetupDraftLocation,
    TSetupPinSource,
    TStoreSetupDraftPatch,
  } from '@/composition/store-setup/use-store-setup-draft';
  import { useGtag } from '@/composition/use-gtag';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { businessTypeFromPlace } from '@/domain/store-setup/business-types';
  import { regionName } from '@/domain/store-setup/display-names';
  import {
    ADDRESS_MAX,
    ADDRESS_MIN,
    COUNTRY_ZOOM,
    PLACE_ZOOM,
    REFERENCE_MAX,
    addressWithoutCountry,
    countryCentre,
    isBusinessPlace,
    isPinValid,
    locationHelperKey,
    locationMethod,
    roundCoord,
  } from '@/domain/store-setup/location-step';
  import type { ILatLng } from '@/domain/store-setup/location-step';
  import { rowsToWeek } from '@/domain/store-setup/opening-hours';
  import {
    currencyForCountry,
    isSupportedCountry,
  } from '@/domain/store-setup/store-country';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IGeoResult, IPlaceDetails } from '@/interfaces/IStoreSetup';
  import { getDevicePosition, isGeolocationGranted } from '@/lib/geolocation';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2SetupCountryChip from './V2SetupCountryChip.vue';
  import V2SetupManualAddress from './V2SetupManualAddress.vue';
  import V2SetupPinMap from './V2SetupPinMap.vue';
  import V2SetupPlaceSearch from './V2SetupPlaceSearch.vue';

  /**
   * Step 1 · «Ubicación» (plan §4.1): find the business or its address,
   * confirm the pin on the map (D5, D6), and the address customers see.
   * Everything the owner does is patched into the local draft; a business
   * pick also prefills «Negocio» (name, type, hours). Exposes the screen
   * handle of the page's bottom bar.
   */
  type TLocationMode = 'search' | 'picked' | 'manual';

  interface IPlaceMeta {
    placeId: string | null;
    placeName: string | null;
    primaryType: string | null;
    types: string[];
    province: string | null;
    city: string | null;
  }

  interface IMapView {
    center: ILatLng;
    zoom: number;
  }

  /** Narrow tiers: the map takes this share of the stable height after a pick. */
  const MAP_HEIGHT_SHARE = 0.55;

  const props = withDefaults(
    defineProps<{
      draft: IStoreSetupDraft;
      places: IUsePlacesSearch;
      wide: boolean;
      /** A gate is open (partner notice): nothing can be searched or confirmed. */
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'patch', patch: TStoreSetupDraftPatch): void;
    (e: 'done'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const gtag = useGtag();
  const ui = useUi();
  const { height } = useStableWindowSize();

  const locale = computed(() => proxy.$i18n.locale);
  const country = computed(() => props.draft.country);

  function emptyMeta(): IPlaceMeta {
    return {
      placeId: null,
      placeName: null,
      primaryType: null,
      types: [],
      province: null,
      city: null,
    };
  }

  function countryView(code: string): IMapView {
    return {
      center: countryCentre(code) ?? { lat: 0, lng: 0 },
      zoom: COUNTRY_ZOOM,
    };
  }

  //#region State (restored from the draft: it only ever stores a valid pin)
  const stored = props.draft.location;
  const mode = ref<TLocationMode>(
    stored === null
      ? 'search'
      : stored.pinSource === 'place'
      ? 'picked'
      : 'manual'
  );
  const pinSource = ref<TSetupPinSource | null>(stored?.pinSource ?? null);
  const pin = ref<ILatLng | null>(
    stored ? { lat: stored.lat, lng: stored.lng } : null
  );
  const meta = ref<IPlaceMeta>(
    stored
      ? {
          placeId: stored.placeId,
          placeName: stored.placeName,
          primaryType: stored.primaryType,
          types: stored.types,
          province: stored.province,
          city: stored.city,
        }
      : emptyMeta()
  );
  const address = ref(stored?.address ?? '');
  /** The address as the picked place brought it (the picked card's line). */
  const pickedAddress = ref(stored?.address ?? '');
  const reference = ref(stored?.reference ?? '');
  /** Typed by the owner: a reverse geocode never overwrites it. */
  const addressEdited = ref(stored !== null);
  const addressTouched = ref(false);
  /** A restored manual pin was valid when stored, so it was moved already. */
  const mapMoved = ref(stored?.pinSource === 'manual');
  const manualMapOpen = ref(stored !== null && mode.value === 'manual');
  const view = ref<IMapView>(
    stored
      ? { center: { lat: stored.lat, lng: stored.lng }, zoom: PLACE_ZOOM }
      : countryView(props.draft.country)
  );
  const searchActive = ref(false);
  const searchFailed = ref(false);
  /** The name of a country we do not work in, after a pick or a device position there. */
  const unsupported = ref<string | null>(null);
  const locating = ref(false);
  const pinMap = ref<{ refresh(): void } | null>(null);
  const root = ref<HTMLElement | null>(null);
  const addressId = nextDomId('setup-location-address');
  const referenceId = nextDomId('setup-location-reference');
  let alive = true;
  //#endregion

  //#region Derived
  const pinVisible = computed(
    () =>
      mode.value === 'picked' ||
      (mode.value === 'manual' && manualMapOpen.value)
  );
  const pinValid = computed(
    () =>
      pinVisible.value &&
      pin.value !== null &&
      isPinValid(pinSource.value, mapMoved.value, view.value.zoom)
  );
  const addressValid = computed(
    () => address.value.trim().length >= ADDRESS_MIN
  );
  const canConfirm = computed(
    () =>
      !props.disabled &&
      pinValid.value &&
      addressValid.value &&
      isSupportedCountry(country.value)
  );
  const addressError = computed(() =>
    addressTouched.value && !addressValid.value
      ? tr('storeSetup.location.addressRequired')
      : undefined
  );
  /** What the draft stores: only a valid pin (D6), with everything typed so far. */
  const location = computed<IStoreSetupDraftLocation | null>(() => {
    if (!pinValid.value || !pin.value || !pinSource.value) return null;
    return {
      ...meta.value,
      address: address.value,
      reference: reference.value,
      lat: pin.value.lat,
      lng: pin.value.lng,
      pinSource: pinSource.value,
    };
  });
  const typeLabel = computed(() =>
    isBusinessPlace(meta.value.types)
      ? tr(
          `storeSetup.business.types.${businessTypeFromPlace(
            meta.value.primaryType,
            meta.value.types
          )}`
        )
      : null
  );
  const pickedName = computed(
    () => meta.value.placeName ?? pickedAddress.value
  );
  const pickedLine = computed(() =>
    typeLabel.value
      ? [typeLabel.value, pickedAddress.value].filter(Boolean).join(' · ')
      : pickedAddress.value
  );
  /**
   * «Usar mi ubicación» of the manual panel: on wide screens the map's own
   * button is enough while its overlay shows (one action, not two about
   * 800 px apart; review round 4, m11). Narrow screens keep it: the map sits
   * below the panel.
   */
  const panelLocateShown = computed(() => !(props.wide && pinVisible.value));
  /** Narrow tiers hide the map while the owner searches (never the input). */
  const mapShown = computed(() => {
    if (props.wide) return true;
    if (mode.value === 'search')
      return !searchActive.value && !props.places.query.value.trim();
    return pinVisible.value;
  });
  const mapStyle = computed(() =>
    props.wide
      ? undefined
      : { height: `${Math.round(height.value * MAP_HEIGHT_SHARE)}px` }
  );
  const bar = computed<IFlowBarState>(() =>
    flowBar({
      helper: tr(
        locationHelperKey({
          pinVisible: pinVisible.value,
          pinValid: pinValid.value,
          source: pinSource.value,
          moved: mapMoved.value,
          zoom: view.value.zoom,
          addressValid: addressValid.value,
        })
      ),
      primaryLabel: tr('storeSetup.location.confirm'),
      primaryDisabled: !canConfirm.value,
    })
  );
  //#endregion

  //#region Pin
  function clearPick() {
    pin.value = null;
    pinSource.value = null;
    meta.value = emptyMeta();
    address.value = '';
    pickedAddress.value = '';
    reference.value = '';
    addressEdited.value = false;
    addressTouched.value = false;
    mapMoved.value = false;
    manualMapOpen.value = false;
  }

  function placePin(point: ILatLng, source: TSetupPinSource) {
    pin.value = point;
    pinSource.value = source;
    mapMoved.value = false;
    manualMapOpen.value = true;
    view.value = { center: point, zoom: PLACE_ZOOM };
  }

  function roundPoint(point: ILatLng): ILatLng {
    return { lat: roundCoord(point.lat), lng: roundCoord(point.lng) };
  }

  function onMove(next: { lat: number; lng: number; zoom: number }) {
    view.value = { center: { lat: next.lat, lng: next.lng }, zoom: next.zoom };
    if (!pinVisible.value) return;
    pin.value = { lat: next.lat, lng: next.lng };
    mapMoved.value = true;
  }

  /** A pin the owner places by hand: the device position when already allowed, else the country (D6). */
  async function openManualPin() {
    const centre = countryCentre(country.value) ?? view.value.center;
    meta.value = emptyMeta();
    pin.value = centre;
    pinSource.value = 'manual';
    mapMoved.value = false;
    manualMapOpen.value = true;
    view.value = { center: centre, zoom: COUNTRY_ZOOM };
    if (!(await isGeolocationGranted()) || !alive) return;
    try {
      const position = roundPoint(await getDevicePosition());
      // The owner may have moved the map or left the manual pin meanwhile.
      if (
        !alive ||
        mapMoved.value ||
        mode.value !== 'manual' ||
        pinSource.value !== 'manual'
      )
        return;
      pin.value = position;
      view.value = { center: position, zoom: PLACE_ZOOM };
    } catch {
      // Stays on the country: the owner zooms in by hand.
    }
  }
  //#endregion

  //#region Country
  async function onCountry(code: string) {
    if (code === country.value) return;
    emit('patch', { country: code, currency: currencyForCountry(code) });
    clearPick();
    mode.value = 'search';
    searchFailed.value = false;
    unsupported.value = null;
    view.value = countryView(code);
    // The search composable re-scopes itself on the new country first.
    await nextTick();
    if (alive && mode.value === 'search') props.places.search();
  }
  //#endregion

  //#region Search
  function onPick(details: IPlaceDetails) {
    const placeCountry = details.country ?? country.value;
    if (!isSupportedCountry(placeCountry)) {
      unsupported.value = regionName(placeCountry, locale.value);
      return;
    }
    unsupported.value = null;
    const patch: TStoreSetupDraftPatch = {};
    if (placeCountry !== country.value) {
      patch.country = placeCountry;
      patch.currency = currencyForCountry(placeCountry);
    }
    if (isBusinessPlace(details.types)) {
      patch.name = details.name;
      patch.businessType = businessTypeFromPlace(
        details.primaryType,
        details.types
      );
      patch.businessTypeFromGoogle = true;
      if (details.hours && details.hours.length) {
        patch.hours = rowsToWeek(details.hours);
        patch.hoursFromGoogle = true;
        patch.hoursConfirmed = false;
      } else if (props.draft.hoursFromGoogle) {
        // Hours of a previously picked place do not belong to this one.
        patch.hours = null;
        patch.hoursFromGoogle = false;
        patch.hoursConfirmed = false;
      }
    }
    if (Object.keys(patch).length) emit('patch', patch);
    meta.value = {
      placeId: details.placeId,
      placeName: details.name,
      primaryType: details.primaryType,
      types: details.types,
      province: details.province,
      city: details.city,
    };
    const prefill = addressWithoutCountry(
      details.formattedAddress,
      placeCountry,
      locale.value
    );
    address.value = prefill;
    pickedAddress.value = prefill;
    reference.value = '';
    addressEdited.value = false;
    addressTouched.value = false;
    searchFailed.value = false;
    placePin(roundPoint(details), 'place');
    mode.value = 'picked';
  }

  function enterManual(failed: boolean) {
    const typed = props.places.query.value.trim();
    clearPick();
    // Search is down: what was typed is probably the address.
    if (failed && typed) address.value = typed.slice(0, ADDRESS_MAX);
    searchFailed.value = failed;
    unsupported.value = null;
    mode.value = 'manual';
  }

  async function backToSearch() {
    clearPick();
    unsupported.value = null;
    mode.value = 'search';
    await nextTick();
    if (!alive) return;
    props.places.search();
    const input = root.value?.querySelector('.search-panel input');
    if (input instanceof HTMLInputElement) input.focus({ preventScroll: true });
  }
  //#endregion

  //#region Manual address
  function onAddressInput(value: string) {
    address.value = value;
    addressEdited.value = true;
  }

  function onResolved(result: IGeoResult | null) {
    if (!result) {
      void openManualPin();
      return;
    }
    meta.value = {
      ...emptyMeta(),
      province: result.province,
      city: result.city,
    };
    placePin(roundPoint(result), 'geocode');
  }

  function onGeocodeFailed() {
    // D6: a valid pin already on the map stays; otherwise the manual pin is the way forward.
    if (!pinValid.value) void openManualPin();
  }
  //#endregion

  //#region Device position
  async function fillAddressFrom(point: ILatLng) {
    if (addressEdited.value || address.value.trim()) return;
    const result = await props.places.reverse(point.lat, point.lng);
    // Only while that position is still the pin.
    if (!alive || !result || pinSource.value !== 'geolocation') return;
    if (result.country && !isSupportedCountry(result.country)) {
      clearPick();
      mode.value = 'search';
      unsupported.value = regionName(result.country, locale.value);
      return;
    }
    if (result.country && result.country !== country.value)
      emit('patch', {
        country: result.country,
        currency: currencyForCountry(result.country),
      });
    meta.value = {
      ...meta.value,
      province: result.province,
      city: result.city,
    };
    if (!addressEdited.value && !address.value.trim())
      address.value = addressWithoutCountry(
        result.formattedAddress,
        result.country ?? country.value,
        locale.value
      );
  }

  /** The owner used a «Usar mi ubicación» of the left panel (search or manual). */
  function panelLocateFocused(): boolean {
    const active = document.activeElement;
    return (
      active instanceof HTMLElement &&
      active.classList.contains('locate-button') &&
      !!root.value?.contains(active)
    );
  }

  /** The panel's button just hid under the owner's focus: the map's own «Usar mi ubicación» takes it. */
  async function focusMapLocate() {
    await nextTick();
    if (!alive || panelLocateShown.value) return;
    const button = root.value?.querySelector('.map-locate');
    if (button instanceof HTMLElement) button.focus({ preventScroll: true });
  }

  async function useMyLocation() {
    if (locating.value || props.disabled) return;
    locating.value = true;
    let point: ILatLng;
    try {
      point = roundPoint(await getDevicePosition());
    } catch {
      if (alive) {
        locating.value = false;
        ui.showToast(AlertType.Error, tr('storeSetup.location.geoDenied'));
      }
      return;
    }
    if (!alive) return;
    try {
      if (mode.value === 'picked') {
        // An adjustment of the picked place.
        pin.value = point;
        mapMoved.value = true;
        view.value = { center: point, zoom: PLACE_ZOOM };
        return;
      }
      if (mode.value === 'search') {
        clearPick();
        mode.value = 'manual';
        searchFailed.value = false;
        unsupported.value = null;
      }
      meta.value = emptyMeta();
      const locateFocused = panelLocateFocused();
      placePin(point, 'geolocation');
      if (locateFocused) void focusMapLocate();
      await fillAddressFrom(point);
    } finally {
      if (alive) locating.value = false;
    }
  }
  //#endregion

  //#region Screen handle
  async function primary() {
    addressTouched.value = true;
    const value = location.value;
    if (!canConfirm.value || !value) return;
    emit('patch', {
      location: {
        ...value,
        address: value.address.trim(),
        reference: value.reference.trim(),
      },
    });
    gtag.event('location_confirmed', {
      method: locationMethod(value.pinSource, value.types),
      country: country.value,
    });
    emit('done');
  }

  function nothing(): Promise<void> {
    return Promise.resolve();
  }

  defineExpose({ bar, primary, secondary: nothing, back: nothing });
  //#endregion

  // The draft follows every change of the pin and the typed fields (a valid pin or nothing).
  watch(location, (value) => emit('patch', { location: value }));
  // Two unavailable search answers in a row: the owner types the address instead.
  watch(
    () => props.places.failures.value,
    (count) => {
      if (count >= 2 && mode.value === 'search') enterManual(true);
    }
  );
  // The map lives under v-show and changes size with the tier: re-measure it.
  watch([mapShown, () => props.wide, height], async () => {
    await nextTick();
    if (alive) pinMap.value?.refresh();
  });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div
    ref="root"
    class="v2-setup-step-location"
    :class="wide ? 'is-wide' : 'is-narrow'"
  >
    <div class="panel-surface" aria-hidden="true" />
    <div class="panel-top">
      <slot name="notice" />
      <header class="step-head">
        <h1 class="step-title" tabindex="-1">
          {{ $t('storeSetup.location.title') }}
        </h1>
        <p class="step-subtitle">{{ $t('storeSetup.location.subtitle') }}</p>
      </header>
      <V2SetupCountryChip
        :value="country"
        :disabled="disabled"
        @input="onCountry"
      />
      <div v-show="mode === 'search'" class="search-panel">
        <V2SetupPlaceSearch
          :country="country"
          :places="places"
          :disabled="disabled"
          @pick="onPick"
          @manual="enterManual(false)"
          @focus="searchActive = $event"
        />
        <p v-if="unsupported" class="step-message is-error" role="alert">
          {{ $t('storeSetup.location.unsupported', { country: unsupported }) }}
        </p>
        <PosUiButton
          class="locate-button"
          color="neutral"
          variant="ghost"
          icon="crosshairs-gps"
          :disabled="disabled || locating"
          @click="useMyLocation"
          >{{ $t('storeSetup.location.useMyLocation') }}</PosUiButton
        >
      </div>
      <div v-show="mode === 'picked'" class="picked-card">
        <span class="picked-icon" aria-hidden="true">
          <PosUiIcon name="check" />
        </span>
        <div class="picked-text">
          <p class="picked-name">{{ pickedName }}</p>
          <p v-if="pickedLine" class="picked-line">{{ pickedLine }}</p>
        </div>
        <button
          type="button"
          class="text-link"
          :disabled="disabled"
          @click="backToSearch"
        >
          {{ $t('storeSetup.location.change') }}
        </button>
      </div>
      <div v-show="mode === 'manual'" class="manual-panel">
        <p v-if="searchFailed" class="step-message is-warning" role="status">
          <PosUiIcon name="alert-outline" class="message-icon" />
          <span>{{ $t('storeSetup.location.searchFailed') }}</span>
        </p>
        <h2 class="manual-title">
          {{ $t('storeSetup.location.manualTitle') }}
        </h2>
        <V2SetupManualAddress
          :country="country"
          :value="address"
          :geocode="places.geocode"
          :disabled="disabled"
          @input="onAddressInput"
          @resolved="onResolved"
          @failed="onGeocodeFailed"
        />
        <p v-if="pinVisible && pinSource === 'manual'" class="pin-help">
          {{ $t('storeSetup.location.pinHelp') }}
        </p>
        <div class="manual-links">
          <PosUiButton
            v-show="panelLocateShown"
            class="locate-button"
            color="neutral"
            variant="ghost"
            icon="crosshairs-gps"
            :disabled="disabled || locating"
            @click="useMyLocation"
            >{{ $t('storeSetup.location.useMyLocation') }}</PosUiButton
          >
          <button
            v-if="!searchFailed"
            type="button"
            class="text-link"
            :disabled="disabled"
            @click="backToSearch"
          >
            {{ $t('storeSetup.location.searchLabel') }}
          </button>
        </div>
      </div>
    </div>
    <div v-show="mapShown" class="map-area" :style="mapStyle">
      <V2SetupPinMap
        ref="pinMap"
        :center="view.center"
        :zoom="view.zoom"
        :overlay="pinVisible"
        @move="onMove"
        @locate="useMyLocation"
      />
    </div>
    <div v-show="mode !== 'search'" class="panel-fields">
      <PosUiField
        v-show="mode === 'picked'"
        :label="$t('storeSetup.location.address')"
        :input-id="addressId"
        :error="addressError"
      >
        <PosUiInput
          :id="addressId"
          :value="address"
          :maxlength="ADDRESS_MAX"
          autocomplete="off"
          :disabled="disabled"
          @input="onAddressInput"
          @blur="addressTouched = true"
        />
      </PosUiField>
      <PosUiField
        :label="$t('storeSetup.location.reference')"
        :input-id="referenceId"
      >
        <PosUiInput
          :id="referenceId"
          :value="reference"
          :maxlength="REFERENCE_MAX"
          :placeholder="$t('storeSetup.location.referencePlaceholder')"
          autocomplete="off"
          :disabled="disabled"
          @input="reference = $event"
        />
      </PosUiField>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-step-location {
    @apply tw-mx-auto tw-w-full;
    // Narrow: one white card, top → map → fields (the map sits above the address fields).
    &.is-narrow {
      @apply tw-flex tw-max-w-2xl tw-flex-col tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
      .panel-surface {
        @apply tw-hidden;
      }
      .panel-top {
        @apply tw-p-4;
      }
      .map-area {
        @apply tw-mx-4 tw-mb-4 tw-rounded-lg;
      }
      .panel-fields {
        @apply tw-px-4 tw-pb-4;
      }
    }
    // Wide: a 26 rem panel beside a map that fills the rest.
    &.is-wide {
      @apply tw-grid tw-max-w-screen-2xl tw-flex-auto tw-gap-x-6;
      grid-template-columns: 26rem minmax(0, 1fr);
      grid-template-rows: auto minmax(0, 1fr);
      min-height: 35rem;
      .panel-surface {
        @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
        grid-column: 1;
        grid-row: 1 / span 2;
      }
      .panel-top {
        @apply tw-relative tw-p-6 tw-pb-4;
        grid-column: 1;
        grid-row: 1;
      }
      .panel-fields {
        @apply tw-relative tw-self-start tw-px-6 tw-pb-6;
        grid-column: 1;
        grid-row: 2;
      }
      .map-area {
        @apply tw-rounded-xl;
        grid-column: 2;
        grid-row: 1 / span 2;
        min-height: 35rem;
      }
    }
    .panel-top {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-items-stretch tw-gap-4;
      > .v2-setup-country-chip {
        @apply tw-self-start;
      }
    }
    .step-head {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-bold tw-text-neutral-900 md:tw-text-2xl;
    }
    .step-subtitle {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500 md:tw-text-base;
    }
    .search-panel,
    .manual-panel {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    }
    .manual-panel {
      @apply tw-gap-3;
    }
    .manual-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .locate-button {
      @apply tw-self-start;
    }
    .manual-links {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1;
    }
    .step-message {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2 tw-text-sm;
      &.is-error {
        @apply tw-text-red-700;
      }
      &.is-warning {
        @apply tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-neutral-800;
      }
      .message-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base tw-text-amber-700;
      }
    }
    .pin-help {
      @apply tw-m-0 tw-text-sm tw-text-neutral-600;
    }
    .picked-card {
      @apply tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3;
    }
    .picked-icon {
      @apply tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-emerald-50 tw-text-lg tw-text-emerald-700;
    }
    .picked-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
    }
    .picked-name {
      @apply tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .picked-line {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .text-link {
      @apply tw-min-h-11 tw-shrink-0 tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .map-area {
      @apply tw-relative tw-min-w-0 tw-overflow-hidden tw-border tw-border-solid tw-border-neutral-200;
    }
    .panel-fields {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-4;
    }
  }
</style>
